import { buttonOf, drawSavedWord } from "./save-word-control.js";
import { askHeldWords } from "./save-word-route.js";
import { hasSession, startSaveWords } from "./save-word.js";
import { YueduShengci } from "./yuedu-shengci.js";
import { type PopoverElement, YueduWordPopover } from "./yuedu-word-popover.js";
import { YueduWordRows } from "./yuedu-word-rows.js";

/**
 * Starting a passage's words on its `/yuedu/` page, the text and the questions: the
 * popover on every word, and a signed-in reader's 生词.
 *
 * A visitor makes no request. They get the popover and every reading, and the
 * stylesheet shows them the line offering an account where the list would be.
 * A signed-in reader's page asks `/user/ci/words/` once which of the passage's
 * words they hold, the same question the word segmenter asks, and marks the
 * rest (`yuedu-shengci.ts`). Where that cannot be answered the passage is
 * marked `unknown`, which shows every reading and no list. A session the
 * hint cookie outlived is answered as nobody, and the passage is marked
 * `signed-out`, which shows the reader what a visitor sees. Until the marks
 * are on, and for good where they never arrive, a popover asks about its own
 * word, since a Save drawn over a word the reader holds would remove it.
 *
 * **The text and its questions are two parts of one page** (CB-64). Each
 * carries `data-cb-yuedu-passage`, and the list of 生词 sits between them,
 * outside both, since its rows carry `data-cb-word` as the words do and a
 * press on a row's Save must not open the popover. Every part is marked and
 * listened to alike. The words of the questions the lines lack are asked
 * about in the same request, from the `data-cb-word` they carry.
 *
 * Every save control this draws is bound with {@link startSaveWords}, the 词典's
 * own, told what the reader holds so that it asks nothing, and every press it
 * takes goes back through {@link YueduShengci.change}, so a word saved from the
 * popover loses its mark in the text and its row says Saved.
 * @param document - The page.
 * @param fetched - What asks the route, which a test hands its own.
 * @returns Once the marks are on, or at once for a visitor.
 */
/** Every word the page could mark, those with rows first, once each. */
function markable(
  passages: readonly HTMLElement[],
  rows: YueduWordRows,
): string[] {
  return [
    ...new Set([
      ...rows.words(),
      ...passages.flatMap((passage) =>
        [...passage.querySelectorAll<HTMLElement>("[data-cb-word]")].map(
          (word) => word.dataset["cbWord"] ?? "",
        ),
      ),
    ]),
  ].filter((word) => word !== "");
}

export async function startYueduWords(
  document: Document,
  fetched: typeof fetch = globalThis.fetch,
): Promise<void> {
  const rows = YueduWordRows.found(document);
  const passages = [
    ...document.querySelectorAll<HTMLElement>("[data-cb-yuedu-passage]"),
  ];
  const section = document.querySelector<HTMLElement>(
    "[data-cb-yuedu-shengci]",
  );
  const popover = document.querySelector<PopoverElement>(
    "[data-cb-yuedu-popover]",
  );

  if (rows === undefined || passages.length === 0 || popover === null) {
    return;
  }

  const signedIn = hasSession(document);
  // Filled once the route has answered, and read by every press after that.
  const marks: { shengci?: YueduShengci } = {};

  // A press the popover took before the answer came, kept by word to be put
  // over that answer, which was asked for before the press was made.
  const early = new Map<string, boolean>();
  const onChange = (changed: { word: string; held: boolean }): void => {
    if (marks.shengci === undefined) {
      early.set(changed.word, changed.held);
    } else {
      marks.shengci.change(changed, document);
    }
  };

  // Listening before anything is asked, so a word pressed while the answer is
  // on its way opens at once.
  const words = new YueduWordPopover({
    popover,
    rows,
    bindSave: (within, word): void => {
      const control = within.querySelector<HTMLElement>(".cb-save-word");
      const button = control === null ? null : buttonOf(control);

      if (button !== null && marks.shengci !== undefined) {
        drawSavedWord(button, marks.shengci.holds(word));
      }

      startSaveWords(document, fetched, {
        within,
        ask: signedIn && marks.shengci === undefined,
        onChange,
      });
    },
  });

  for (const passage of passages) {
    words.listen(passage);
  }

  if (!signedIn) {
    return;
  }

  let signedOut = false;
  const held = await askHeldWords(fetched, markable(passages, rows), () => {
    signedOut = true;
  }).catch(() => undefined);

  if (held === undefined) {
    for (const passage of passages) {
      passage.dataset["cbShengci"] = signedOut ? "signed-out" : "unknown";
    }

    return;
  }

  const shengci = new YueduShengci({ passages, section, rows });

  const list = shengci.start(held);

  marks.shengci = shengci;

  for (const [word, holds] of early) {
    shengci.change({ word, held: holds }, document);
  }

  if (list !== null) {
    startSaveWords(document, fetched, { within: list, ask: false, onChange });
  }
}

/**
 * Bring the address's fragment into view again, once the words are marked.
 *
 * `/user/lianxi/` and 学习 link a reading Exercise at `#questions` on its
 * passage's page. The browser scrolls there as the page loads, smoothly
 * where motion is welcome, and marking the 生词 then changes the height of
 * every line over the questions. In Chromium that left the reader at the top
 * of the text. This scrolls to the target again at once, with nothing
 * animated, after which nothing above it moves.
 * @param document - The page.
 */
export function settleFragment(document: Document): void {
  const id = decodeURIComponent(document.location.hash.slice(1));
  const target = id === "" ? null : document.getElementById(id);

  target?.scrollIntoView({ behavior: "instant", block: "start" });
}
